import { API, deleteRequest, getRequest, patchRequest, postRequest } from '@/api/api';
import type { ApiException } from '@/common/error';

import {
  mapCancelSubscriptionToDto,
  mapCreateCheckoutToDto,
  mapDtoToPrice,
  mapDtoToReferral,
  mapDtoToSubscription,
  mapUpdateSubscriptionToDto,
} from './subscriptions.mappers';
import type {
  CancelSubscription,
  CreateCheckoutSubscription,
  Price,
  PriceDto,
  Referral,
  ReferralDto,
  Subscription,
  SubscriptionDto,
  SubscriptionsList,
  SubscriptionsPageDto,
  StripeUrlDto,
  UpdateSubscription,
} from './subscriptions.types';

/**
 * The api client throws the ErrorResponse body rather than the HTTP response, so the
 * status code is not available here — a 404 has to be recognised from `type`, matching
 * the backend's NOT_FOUND convention (see the JWT/NOT_FOUND pair quoted in api.ts).
 * Worth confirming against a live 404 from this endpoint.
 */
const isNotFoundError = (error: unknown): boolean =>
  !!error && typeof error === 'object' && (error as ApiException).type === 'NOT_FOUND';

/**
 * GET /subscriptions → one service's single subscription.
 * A service with no subscription answers 404 rather than an empty body, so that is
 * mapped to `null` — it is a normal state, not a failure.
 *
 * `serviceId` overrides the `X-Service-Id` header the client would otherwise attach for the
 * active account, so a screen spanning several vessels can ask about each of them. Omitted,
 * it reads the active service as before.
 */
export const getSubscription = async (serviceId?: string): Promise<Subscription | null> => {
  try {
    const { data } = await getRequest<SubscriptionDto>(
      API.PEOPLE,
      '/subscriptions',
      false,
      undefined,
      serviceId,
    );
    return mapDtoToSubscription(data);
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

/**
 * GET /subscriptions/all → every subscription the caller is *billed for*, newest first.
 *
 * Filtered on the row's `person_id`, so it is not the set of vessels whose plans the caller
 * manages: a plan a captain pays for is theirs and not their owner's, and vice versa. A
 * screen about vessels reads each vessel's own row through `getSubscription` instead.
 */
export const getAllSubscriptions = async (
  limit: number,
  page: number,
): Promise<SubscriptionsList> => {
  const { data } = await getRequest<SubscriptionsPageDto>(API.PEOPLE, '/subscriptions/all', false, {
    limit,
    page,
  });
  return {
    count: data.count,
    items: data.items.map(mapDtoToSubscription),
  };
};

/**
 * How `/subscriptions/all` is walked when the whole set is wanted. The page size is large
 * because the set is small — one row per service the caller pays for — and the cap only exists
 * so a server that keeps answering with a full page cannot spin forever.
 */
const BILLED_PAGE_SIZE = 100;
const BILLED_MAX_PAGES = 20;

/**
 * The ids of every service the caller is billed for, folded out of `/subscriptions/all`.
 *
 * The only way to tell whose card a vessel's plan is on: `GET /subscriptions` describes the
 * plan and never its payer — `SubscriptionResponse.PersonID` is `json:"-"` in the People
 * service's `pkg/dto/subscription.go`, so no field on it names one — while this endpoint
 * filters on exactly that column. A vessel missing from the set is on somebody else's plan:
 * a captain's, most often, which the owner may take over by buying it in the billing portal.
 *
 * Paged to the end rather than read one page deep, so a fleet does not report its own vessels
 * as somebody else's once they run past a page.
 */
export const getBilledServiceIds = async (): Promise<Set<string>> => {
  const serviceIds = new Set<string>();
  let read = 0;

  for (let page = 1; page <= BILLED_MAX_PAGES; page++) {
    const { count, items } = await getAllSubscriptions(BILLED_PAGE_SIZE, page);

    items.forEach((subscription) => serviceIds.add(subscription.serviceId));
    read += items.length;

    if (items.length === 0 || read >= count) break;
  }

  return serviceIds;
};

/** GET /subscriptions/prices — `lookupKeys` is a CSV query param, not repeated keys. */
export const getSubscriptionPrices = async (lookupKeys: string[]): Promise<Price[]> => {
  const { data } = await getRequest<PriceDto[]>(API.PEOPLE, '/subscriptions/prices', false, {
    lookupKeys: lookupKeys.join(','),
  });
  return data.map(mapDtoToPrice);
};

/**
 * POST /subscriptions/checkout → Stripe checkout URL to redirect the user to.
 *
 * The subscription row is created as `incomplete` the moment this resolves — before
 * the user has paid. It only flips to active once Stripe's webhook lands, so a user
 * who abandons the Stripe page leaves an incomplete row behind.
 *
 * Rejects with 403 when the caller already has the maximum number of paid services,
 * which needs a "contact sales" branch rather than an error toast. That case is not
 * distinguishable from a 400 here: the api client discards the HTTP status and the
 * spec documents no domain/type for it.
 */
export const createSubscriptionCheckout = async (
  values: CreateCheckoutSubscription,
): Promise<string> => {
  const { data } = await postRequest<StripeUrlDto, unknown>(
    API.PEOPLE,
    '/subscriptions/checkout',
    mapCreateCheckoutToDto(values),
  );
  return data.url;
};

/**
 * PATCH /subscriptions/{id} — change tier, interval or seat counts.
 *
 * Answers either 204 (applied) or 200 with a Stripe URL (the subscription was on
 * `harbor`, which has no Stripe object to modify, so the server started a checkout
 * instead). Returns that URL when present, `null` when the change was applied.
 *
 * Seat proration is asymmetric: increases invoice immediately, decreases carry a
 * credit instead of refunding.
 *
 * A 502 here means Stripe could not price the change and nothing was altered, so it is
 * safe to retry — but, as with the 403 above, it cannot currently be told apart from a
 * 500 because the thrown error carries no status.
 */
export const updateSubscription = async (
  id: string,
  values: UpdateSubscription,
): Promise<string | null> => {
  const response = await patchRequest<StripeUrlDto, unknown>(
    API.PEOPLE,
    `/subscriptions/${id}`,
    mapUpdateSubscriptionToDto(values),
  );
  return response.status === 200 ? (response.data?.url ?? null) : null;
};

/**
 * POST /subscriptions/{id}/cancel — schedules cancellation at the end of the current
 * period. The subscription stays active until `cancelAt`.
 */
export const cancelSubscription = async (id: string, values: CancelSubscription = {}) => {
  await postRequest(API.PEOPLE, `/subscriptions/${id}/cancel`, mapCancelSubscriptionToDto(values));
};

/** DELETE /subscriptions/{id}/uncancel — clears a pending cancellation. */
export const uncancelSubscription = async (id: string) => {
  await deleteRequest(API.PEOPLE, `/subscriptions/${id}/uncancel`);
};

/**
 * GET /me → the caller's referral link, head count and referrer.
 *
 * Read from the person record because the people service has no referral endpoint: the
 * three fields live on `dto.PersonResponse` and nothing else exposes them. `/me` is also
 * fetched by the auth and person stores; this reads it separately so the referral data
 * stays owned by this store rather than threaded through those.
 */
export const getReferral = async (): Promise<Referral> => {
  const { data } = await getRequest<ReferralDto>(API.PEOPLE, '/me');
  return mapDtoToReferral(data);
};

/** POST /subscriptions/portal → Stripe billing portal URL (payment methods, invoices). */
export const createSubscriptionPortal = async (): Promise<string> => {
  const { data } = await postRequest<StripeUrlDto, undefined>(
    API.PEOPLE,
    '/subscriptions/portal',
    undefined,
  );
  return data.url;
};
